<script setup>
import { onBeforeUnmount, ref, watch, nextTick, computed } from "vue";
import { usePage } from "@inertiajs/vue3";
import { useAnalyticsStore } from "@/Stores/AnalyticsStore";

const props = defineProps({
	isActive: {
		type: Boolean,
		default: false
	}
});

const analyticsStore = useAnalyticsStore();
const adContainer = ref(null);
const adLoaded = ref(false);
const page = usePage();

// Default AdSense configuration (fallback)
const DEFAULT_AD_CLIENT = 'ca-pub-3839581235117188';
const DEFAULT_AD_UNITS = [
	{ slot: '4658021535', format: 'auto', fullWidthResponsive: true },
	{ slot: '3928243857', format: 'autorelaxed', fullWidthResponsive: false }
];

// Parse custom ad code from settings to extract attributes
const parseAdCode = (htmlCode) => {
	if (!htmlCode) return null;

	// Create a temporary DOM element to parse the HTML
	const tempDiv = document.createElement('div');
	tempDiv.innerHTML = htmlCode;

	const insElement = tempDiv.querySelector('ins.adsbygoogle');
	if (!insElement) return null;

	return {
		client: insElement.dataset.adClient || null,
		slot: insElement.dataset.adSlot || null,
		format: insElement.dataset.adFormat || 'auto',
		fullWidthResponsive: insElement.dataset.fullWidthResponsive === 'true'
	};
};

// Get ad config from settings or use defaults
const getAdConfig = () => {
	const customCode = page.props.settings?.google_ads_infeed_code;
	const parsed = parseAdCode(customCode);

	if (parsed && parsed.client && parsed.slot) {
		return {
			client: parsed.client,
			slot: parsed.slot,
			format: parsed.format,
			fullWidthResponsive: parsed.fullWidthResponsive
		};
	}

	// Fallback to default ad units (50/50 randomization)
	const defaultUnit = DEFAULT_AD_UNITS[Math.random() < 0.5 ? 0 : 1];
	return {
		client: DEFAULT_AD_CLIENT,
		slot: defaultUnit.slot,
		format: defaultUnit.format,
		fullWidthResponsive: defaultUnit.fullWidthResponsive
	};
};

// Load ad when slide becomes active
const loadAd = async () => {
	if (adLoaded.value || !adContainer.value) return;
	adLoaded.value = true;

	const adConfig = getAdConfig();
	console.log('[GoogleAd] Loading AdSense ad, slot:', adConfig.slot);

	// Create the ins element dynamically
	const ins = document.createElement('ins');
	ins.className = 'adsbygoogle';
	ins.style.display = 'block';
	ins.dataset.adClient = adConfig.client;
	ins.dataset.adSlot = adConfig.slot;
	ins.dataset.adFormat = adConfig.format;
	if (adConfig.fullWidthResponsive) {
		ins.dataset.fullWidthResponsive = 'true';
	}

	// Append to container
	adContainer.value.appendChild(ins);

	// Wait for DOM update
	await nextTick();

	// Push to adsbygoogle to load the ad
	try {
		(window.adsbygoogle = window.adsbygoogle || []).push({});
		console.log('[GoogleAd] AdSense push successful');

		analyticsStore.sendAnalyticsEvent('ad_impression', {
			adType: 'google-adsense'
		});
	} catch (e) {
		console.error('[GoogleAd] AdSense error:', e);
	}
};

// Cleanup ad element
const cleanupAd = () => {
	if (adContainer.value) {
		// Remove all child elements (the ins element and any ad content)
		while (adContainer.value.firstChild) {
			adContainer.value.removeChild(adContainer.value.firstChild);
		}
	}
	adLoaded.value = false;
	console.log('[GoogleAd] Ad cleaned up');
};

// Watch for when slide becomes active
watch(() => props.isActive, (active) => {
	console.log('[GoogleAd] isActive changed:', active);
	if (active) {
		loadAd();
	} else {
		// Clean up when slide becomes inactive to prepare for fresh ad on next visit
		cleanupAd();
	}
}, { immediate: true });

// Cleanup on unmount
onBeforeUnmount(() => {
	cleanupAd();
});
</script>

<template>
	<div class="absolute inset-0 flex items-center justify-center overflow-hidden bg-black">
		<div ref="adContainer" class="w-full h-full"></div>
	</div>
</template>
